import { Card, Heading } from '@zillow/constellation-10';

import numberUtils from 'app/shared/utils/numberUtils';
import type React from 'react';
import { useMemo } from 'react';
import QuestionAnswer from './QuestionAnswer';

import type { MarketTrends } from 'app/shared/models/MarketTrends';
import type MedianRentByBedroom from 'app/shared/models/MedianRentByBedroom';

interface MarketFAQProps {
  marketTrends: MarketTrends;
  medianRentByBedroom: MedianRentByBedroom;
  isMobile: boolean;
}

function moreOrLess(amount: number | null): string | null {
  // return null to represent no change
  if (amount === null || amount === 0) {
    return null;
  }
  return amount >= 0 ? 'more' : 'less';
}

function increaseOrDecreased(value: string) {
  if (value !== 'more' && value !== 'less') {
    throw new Error(`Invalid input: ${value}`);
  }
  return value === 'more' ? 'increased' : 'decreased';
}

function higherOrLower(value: string) {
  if (value !== 'more' && value !== 'less') {
    throw new Error(`Invalid input: ${value}`);
  }
  return value === 'more' ? 'higher' : 'lower';
}

export function getFAQ(
  marketTrends: MarketTrends,
  medianRentByBedroom: MedianRentByBedroom,
): Array<{ question: string; answer: string }> {
  if (marketTrends.medianRent === null) {
    return [];
  }
  const { areaName, medianRent, minRent, maxRent, yearlyChange, monthlyChange, availableRentals, medianNationalRent } =
    marketTrends;

  const {
    studioMedianRent,
    oneBedroomMedianRent,
    twoBedroomMedianRent,
    threeBedroomMedianRent,
    fourPlusBedroomMedianRent,
  } = medianRentByBedroom;

  // In the last month, the rent in [city,state,zip] [increased/decreased] by [$ change].
  const monthlyChangeAnswer =
    moreOrLess(monthlyChange) === null
      ? `In the last month, the rent in ${areaName} did not change.`
      : `In the last month, the rent in ${areaName} ${increaseOrDecreased(
          moreOrLess(monthlyChange) as string,
        )} by <strong>${numberUtils.asCurrency(Math.abs(monthlyChange!))}</strong>`;

  const yearlyChangeAnswer =
    moreOrLess(yearlyChange) === null
      ? `In the last year, the rent in ${areaName} did not change.`
      : `In the last year, the rent in ${areaName} ${increaseOrDecreased(
          moreOrLess(yearlyChange) as string,
        )} by <strong>${numberUtils.asCurrency(Math.abs(yearlyChange!))}</strong>`;

  // round all values first for national average FAQ
  const medianNationalRentRounded = Math.round(medianNationalRent!);
  const medianRentRounded = Math.round(medianRent);

  const displayRent = numberUtils.asCurrency(medianRentRounded);
  const displayMedianNationalRentAsCurrency = numberUtils.asCurrency(medianNationalRentRounded);
  const displayStudioRent = studioMedianRent !== null ? numberUtils.asCurrency(Math.round(studioMedianRent)) : null;
  const displayOneBedroomRent =
    oneBedroomMedianRent !== null ? numberUtils.asCurrency(Math.round(oneBedroomMedianRent)) : null;
  const displayTwoBedroomRent =
    twoBedroomMedianRent !== null ? numberUtils.asCurrency(Math.round(twoBedroomMedianRent)) : null;
  const displayThreeBedroomRent =
    threeBedroomMedianRent !== null ? numberUtils.asCurrency(Math.round(threeBedroomMedianRent)) : null;
  const displayFourPlusBedroomRent =
    fourPlusBedroomMedianRent !== null ? numberUtils.asCurrency(Math.round(fourPlusBedroomMedianRent)) : null;

  const diffMedianRentAndNationalRent = medianRentRounded - medianNationalRentRounded;
  const percentDifference = (Math.abs(diffMedianRentAndNationalRent) / medianNationalRentRounded) * 100;
  const percentDifferenceRounded = Math.round(percentDifference * 100) / 100;

  /**
   * conditionally rendered expected output:
    If rent is >1% percentDifference
      The average rent in [city/state/zip] is [$price] — [percent diff] [higher/lower] than the national average of [$price].
    
    If rent is less than 1% percentDifference include “less than 1%”

    If rent price is the same
      The average rent in [city/state/zip] is [$price] — the same as the national average of [$price].
  */
  let nationalAverageAnswer = '';
  let difference;

  if (percentDifferenceRounded > 1) {
    difference = `${percentDifferenceRounded}% ${higherOrLower(moreOrLess(diffMedianRentAndNationalRent) as string)}`;
  } else if (percentDifferenceRounded > 0) {
    difference = `less than 1% ${higherOrLower(moreOrLess(diffMedianRentAndNationalRent) as string)}`;
  } else {
    difference = 'the same as';
  }

  nationalAverageAnswer = `The average rent in ${areaName} is ${displayRent} — ${difference} the national average of ${displayMedianNationalRentAsCurrency}.`;

  return [
    {
      question: `What is the average rent in ${areaName}?`,
      answer: `The average rent in ${areaName} is <strong>${displayRent}.</strong>`,
    },
    studioMedianRent && {
      question: `What is the average rent for a studio apartment in ${areaName}?`,
      answer: `The average rent for a studio apartment in ${areaName} is ${displayStudioRent} per month.`,
    },
    oneBedroomMedianRent && {
      question: `What is the average rent for a one-bedroom apartment in ${areaName}?`,
      answer: `The average rent for a one-bedroom apartment in ${areaName} is ${displayOneBedroomRent} per month.`,
    },
    twoBedroomMedianRent && {
      question: `What is the average rent for a two-bedroom apartment in ${areaName}?`,
      answer: `The average rent for a two-bedroom apartment in ${areaName} is ${displayTwoBedroomRent} per month.`,
    },
    threeBedroomMedianRent && {
      question: `What is the average rent for a three-bedroom apartment in ${areaName}?`,
      answer: `The average rent for a three-bedroom apartment in ${areaName} is ${displayThreeBedroomRent} per month.`,
    },
    fourPlusBedroomMedianRent && {
      question: `What is the average rent for a four-bedroom apartment in ${areaName}?`,
      answer: `The average rent for a four-bedroom apartment in ${areaName} is ${displayFourPlusBedroomRent} per month.`,
    },
    // Skip when there's no real rent range (e.g. areas with no active rentals report min/max of 0),
    // otherwise this renders a nonsensical "$0 - $0".
    !!minRent &&
      !!maxRent && {
        question: `How much does it cost to rent a house in ${areaName}?`,
        answer: `Houses in ${areaName} rent between <strong>${numberUtils.asCurrency(
          minRent,
        )} - ${numberUtils.asCurrency(maxRent)}</strong> with an average rent of ${displayRent}.`,
      },

    // yearlyChange
    {
      question: `How has the rent in ${areaName} changed in the last year?`,
      answer: yearlyChangeAnswer,
    },

    // monthlyChange
    {
      question: `How much has the rent in ${areaName} increased/decreased in the last month?`,
      answer: monthlyChangeAnswer,
    },

    availableRentals !== null && {
      question: `How many available rentals are there in ${areaName}?`,
      answer: `There are currently <strong>${numberUtils.fullNumber(
        availableRentals,
      )}</strong> rentals available in ${areaName}.`,
    },

    // medianNationalRent — skip when national rent is missing/zero; the percentage math would
    // otherwise divide by zero and render "Infinity% higher … of $0".
    medianNationalRentRounded > 0 && {
      question: `How do rent prices in ${areaName} compare with the national average?`,
      answer: nationalAverageAnswer,
    },
  ].filter((qAndA): qAndA is { question: string; answer: string } => !!qAndA);
}

const MarketFAQ: React.FunctionComponent<React.PropsWithChildren<React.PropsWithChildren<MarketFAQProps>>> = (
  props,
) => {
  const { marketTrends, medianRentByBedroom, isMobile } = props;
  const { areaName } = marketTrends;
  const FAQ = useMemo(() => getFAQ(marketTrends, medianRentByBedroom), [marketTrends, medianRentByBedroom]);
  return areaName && FAQ.length ? (
    <Card tone="neutral" css={{ padding: '600', paddingBottom: '1200', marginTop: '600' }}>
      <Heading level={3} textStyle="heading-sm" fontFamily="heading-alt" css={{ marginBottom: '800' }}>
        Frequently asked questions
      </Heading>
      {FAQ.map(({ question, answer }, i) => (
        <QuestionAnswer key={question} question={question} answer={answer} i={i} isMobile={isMobile} />
      ))}
    </Card>
  ) : null;
};

export default MarketFAQ;
